import React, {
  useEffect, useMemo, useState
} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { sortBy } from 'remeda';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useDecision } from '@optimizely/react-sdk';
import LocalizedProductTile from '@/common/components/LocalizedProductTile';
import CategoryHeader from '@/common/components/CategoryHeader';
import CaloriesDisclaimer from '@/common/CaloriesDisclaimer/CaloriesDisclaimer';
import { DisplayableModifier, DisplayableProduct, Product } from '@/domain/product/types';
import CenteredContainer from '@/common/CenteredContainer';
import colors from '@/common/colors';
import useAddToDeal from '../../hooks/useAddToDeal';
import DealType from '../../DealTypes';
import useAddToCart from '@/cart/hooks/useAddToCart';
import { dealSelectors } from '../../slice/deal.slice';
import {
  clearDealRecipes,
  userSelectionsSelector
} from '../../slice/userSelections.slice';
import { openModal } from '@/localization/actions';
import {
  cartErrorModalDetails,
  dealDataOOSModalDetails,
  RETURN_TO_DEALS
} from '@/common/Modal/modalDetails';
import { useApplyDealRulesForNonPizzaProduct } from '@/builders/deals/hooks/useApplyDealRules';
import { onNonPizzaMenuPageLoadInDeal } from '@/menu/categories/categories.analytics';
import { getProductsAsItems } from '@/dataAnalytics/dataAnalyticsHelper';
import Routes from '@/router/routes';
import useDeepEffect from '@/common/useDeepEffect';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';
import { mobileStartBreakpoint } from '@/materialUi/theme';
import { SodiumWarningDisclaimer } from '@/clientCore/components/SodiumWarningDisclaimer/components/SodiumWarningDisclaimer';
import useSodiumAlertForStore from '@/clientCore/components/SodiumWarningDisclaimer/hooks/useSodiumAlertForStore';
import { ButtonProps } from '@/coreComponents/buttons/SelectorButton/SelectorButton';
import { useCCGetDealBuilderQuery } from '@/clientCore/temporaryTransformationalHooks/useCCGetDealBuilderQuery';

const useStyles = makeStyles((theme) => ({
  subheading: {
    fontSize: '14px',
    color: colors.gray900,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: 'open_sans',
    fontWeight: 700,
    lineHeight: '21px',
    paddingTop: '13px',
    [theme.breakpoints.up(mobileStartBreakpoint)]: {
      PaddingTop: 'unset'
    }
  },
  mobileSubheading: {
    margin: '24px 16px'
  },
  tilesGrid: {
    display: 'grid',
    gridTemplateRows: '1fr',
    gridGap: '24px',
    [theme.breakpoints.up(mobileStartBreakpoint)]: {
      marginTop: '24px'
    }
  }
}));

interface ProductMenuStepProps {
  stepName: string;
}

const ProductMenuStep = ({ stepName }: ProductMenuStepProps): JSX.Element => {
  const [{ enabled: isAdditionalSizeUpchargeEnabled }] = useDecision('fr-dtg-1306-dispaly-menu-product-size-upcharge');
  const { id: dealId } = useSelector(dealSelectors.selectDealSummary);
  const { rawData } = useCCGetDealBuilderQuery({ itemId: dealId });

  const currentStep = useSelector(userSelectionsSelector.selectCurrentStep);
  const stepData = rawData?.steps?.find((step) => step.id === currentStep?.id);
  const recipesFromStepData = stepData?.recipes;

  const analytics = useAnalytics();
  const buttonText = 'Add to Deal';
  const singleStepButtonText = 'Add';
  const { deal } = analytics.analyticsDataModel;

  const classes = useStyles();

  const { recipeOptions } = currentStep ?? { recipeOptions: undefined };

  const data = recipeOptions
    ? recipeOptions.reduce((acc: DisplayableProduct[], currentRecipe) => {
      const { displayableProduct } = currentRecipe;
      if (displayableProduct) acc.push(displayableProduct);
      return acc;
    }, [])
    : undefined;

  const [pageLoadAnalyticsPushed, setPageLoadAnalyticsPushed] = useState(false);
  const filteredData = useApplyDealRulesForNonPizzaProduct(
    data
  ) as DisplayableProduct[];
  const addToDeal = useAddToDeal();
  const dealType = useSelector(dealSelectors.selectDealType);
  const dispatch = useDispatch();
  const router = useRouter();
  const { addToCart } = useAddToCart();
  const { isShowSodiumAlertForStore } = useSodiumAlertForStore();

  const productsForAnalytics = useMemo(() => {
    try {
      if (filteredData?.length) {
        return getProductsAsItems(filteredData, 'Deals');
      }
      return [];
    } catch (e) {
      return [];
    }
  }, [filteredData]);

  const hasProductsInStock = useMemo(
    () => filteredData?.length && !!filteredData.find((item) => !item.isOutOfStock),
    [filteredData]
  );

  useEffect(() => {
    if (filteredData?.length && !pageLoadAnalyticsPushed) {
      analytics.push(() => onNonPizzaMenuPageLoadInDeal(
        productsForAnalytics,
        deal.deal_id,
        deal.deal_name
      ));
      setPageLoadAnalyticsPushed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData]);

  useDeepEffect(() => {
    if (filteredData?.length && !hasProductsInStock) {
      dispatch(
        openModal({
          ...dealDataOOSModalDetails,
          cta: {
            text: RETURN_TO_DEALS,
            callback: () => {
              router.push(Routes.DEALS);
            }
          },
          hideCloseIcon: true
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, hasProductsInStock]);

  const productsWithUpcharge = useMemo(() => filteredData.map((product) => {
      const hasMultipleSizes = product?.sizes?.length && product.sizes.length > 1;
      const recipe = recipesFromStepData?.find((stepDataRecipe) => stepDataRecipe.id === product.id);
      if (isAdditionalSizeUpchargeEnabled && hasMultipleSizes) {
        const recipeSizesFromRawData = recipe?.options?.find((option) => option.optionTypeCode?.toLowerCase().includes('size'))?.modifiers;
        const sizesWithUpcharge = product.sizes?.reduce((sizes: DisplayableModifier[], size) => {
          const sizeUpcharge = recipeSizesFromRawData?.find((recipeSize) => recipeSize.id === size.id)?.modifiers?.[0]?.upcharge;

          return [...sizes, { ...size, upcharge: sizeUpcharge ?? 0 }];
        }, []);
        return {
          ...product,
          sizes: sizesWithUpcharge ?? []
        };
      }
      const selectedOption = recipe?.selectedOptions?.find((option) => !!option.variantCode);
      return {
        ...product,
        upcharge: selectedOption?.upcharge ?? 0
      };
    }), [filteredData, recipesFromStepData, isAdditionalSizeUpchargeEnabled]);

  const sortedProducts = useMemo(() => sortBy(
      productsWithUpcharge,
      (product: DisplayableProduct) => product.upcharge ?? 0,
      (product: DisplayableProduct) => product.isOutOfStock ?? 0
    ), [productsWithUpcharge]);

  const isSingleStepDeal = dealType === DealType.SINGLE_STEP;

  const onSuccessAdd = () => {
    router.push(Routes.UPSELL);
  };

  const onFailedAdd = () => {
    dispatch(clearDealRecipes());
  };

  const handleAddButtonClick = async (
    selectedDealItem: Product,
    onSuccess: () => void,
    onFailed: () => void,
    fromProductInfoRail?: boolean,
    upcharge?: number
  ): Promise<boolean> => {
    const updatedDeal = addToDeal(selectedDealItem, fromProductInfoRail, upcharge);
    if (updatedDeal && isSingleStepDeal) {
      const combinedFailed = () => {
        onFailed();
        onFailedAdd();
      };
      const combinedSuccess = () => {
        onSuccess();
        onSuccessAdd();
      };
      await addToCart(updatedDeal, combinedSuccess, combinedFailed);
    }
    return !!updatedDeal;
  };

  const handleErrorModal = () => {
    dispatch(openModal(cartErrorModalDetails));
  };

  const multiStepButtonDetails: ButtonProps = {
    ariaLabel: buttonText,
    ariaLabelLoading: 'Loading add to deal',
    text: buttonText,
    size: 'small',
    action: 'ADD_TO_DEAL',
    color: 'secondary',
    variant: 'outlined'
  };

  return (
    <div data-testid="product-menu-step">
      <CenteredContainer>
        <CategoryHeader
          loadingCategoryName={false}
          pageDisplayName={stepName}
          handleCTAClick={() => null}
          headingText={stepName}
          isLocalized
        />
        <section className={classes.tilesGrid}>
          {sortedProducts.map((product, index: number) => {
              /**
               * @deprecated DTG-1306 This function is deprecated and should be removed once fr-dtg-1306-dispaly-menu-product-size-upcharge is retired.
               * Please use the new handleTileButtonClickV2 function instead.
               */
              const handleTileButtonClick = (
                selectedDealItem: Product,
                onSuccess: () => void,
                onFailed: () => void,
                fromProductInfoRail?: boolean
              ) => handleAddButtonClick(selectedDealItem, onSuccess, onFailed, fromProductInfoRail, product.upcharge);

              const handleTileButtonClickV2 = ({
                product: selectedDealItem,
                onSuccess,
                onFailed,
                fromProductInfoRail = false,
                upcharge = 0
              }: {
                product: Product;
                onSuccess: () => void;
                onFailed: () => void;
                fromProductInfoRail?: boolean;
                upcharge?: number;
              }) => handleAddButtonClick(
                selectedDealItem, onSuccess, onFailed, fromProductInfoRail, upcharge || product.upcharge
              );

              return (
                <LocalizedProductTile
                  productIndex={index}
                  key={`deal-product-tile-${product.id}`}
                  categoryName={stepName}
                  showErrorModal={handleErrorModal}
                  handleAddButtonClick={handleTileButtonClick}
                  handleAddButtonClickV2={handleTileButtonClickV2}
                  showPrice={false}
                  showQuantity={false}
                  buttonDetails={
                    isSingleStepDeal
                      ? { text: singleStepButtonText }
                      : multiStepButtonDetails
                  }
                  telemetryEventName="deal-builder-product"
                  isSingleStepDeal={isSingleStepDeal}
                  {...product}
                />
              );
          })}
        </section>
        <CaloriesDisclaimer />
        {isShowSodiumAlertForStore && <SodiumWarningDisclaimer />}
      </CenteredContainer>
    </div>
  );
};

export default ProductMenuStep;
